import { formatDate, parseISO } from "date-fns"
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "design-system/atoms/Accordion"
import { Badge } from "design-system/atoms/Badge"
import { Separator } from "design-system/atoms/Separator"
import { Skeleton } from "design-system/atoms/Skeleton"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "design-system/molecules/Tabs"
import { GitCompareArrows, MessageSquareText, RefreshCw } from "lucide-react"
import { ZendeskIcon } from "modules/shared/icons/zendesk"
import { useZendeskTicketAuditsQuery } from "modules/support/hooks/useZendeskTicketAuditsQuery"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  ConversationTicketIntegrationKindEnum,
  ConversationTicketIntegrationResponseDataSchema,
  ZendeskTicketAuditsResponseDataSchema,
} from "schema"
import z from "zod"

type Integration = z.infer<typeof ConversationTicketIntegrationResponseDataSchema>

type Props = {
  integrations: Integration[]
} & ({ ticketId: string } | { issueId: string })

function getZendeskAuditsQueryProps(props: Props, enabled: boolean) {
  if ("ticketId" in props) {
    return { ticketId: props.ticketId, enabled }
  }

  return { issueId: props.issueId, enabled }
}

type ZendeskTicketAudits = z.infer<typeof ZendeskTicketAuditsResponseDataSchema>
type ZendeskAudit = ZendeskTicketAudits["audits"][number]
type ZendeskAuditEvent = ZendeskAudit["events"][number]
type ZendeskMessageEvent = {
  audit: ZendeskAudit
  event: ZendeskAuditEvent
}
type ZendeskChangeAudit = {
  audit: ZendeskAudit
  events: ZendeskAuditEvent[]
}

const AUDIT_CHANGE_FIELDS = new Set([
  "assignee_id",
  "brand_id",
  "custom_fields",
  "custom_status_id",
  "group_id",
  "priority",
  "status",
  "tags",
  "ticket_form_id",
  "type",
])

export function ZendeskAuditPanel(props: Props) {
  const { t } = useTranslation()
  const [accordionValue, setAccordionValue] = useState<string>()

  const zendeskIntegration = props.integrations.find(
    (integration) => integration.kind === ConversationTicketIntegrationKindEnum.Zendesk,
  )

  const { data, dataUpdatedAt, isError, isFetching, isPending } = useZendeskTicketAuditsQuery(
    getZendeskAuditsQueryProps(props, Boolean(zendeskIntegration)),
  )

  const changeAudits = useMemo(
    () =>
      data?.audits
        .map((audit) => ({
          audit,
          events: audit.events.filter(isChangeEvent),
        }))
        .filter(({ events }) => events.length > 0) ?? [],
    [data],
  )

  const messageEvents = useMemo(
    () =>
      data?.audits.flatMap((audit) =>
        audit.events.filter(isMessageEvent).map((event) => ({ audit, event })),
      ) ?? [],
    [data],
  )

  if (!zendeskIntegration) return null

  const fetchedAt = dataUpdatedAt ? formatDate(dataUpdatedAt, "p") : null

  return (
    <div className="flex flex-col gap-4">
      <Accordion
        type="single"
        collapsible
        value={accordionValue}
        onValueChange={setAccordionValue}
        className="rounded-md border border-support-primary bg-muted/20"
      >
        <AccordionItem value="zendesk-audit" className="border-b-0">
          <AccordionTrigger className="px-4 py-2 bg-support-primary/5 data-[state=open]:rounded-b-none">
            <div className="flex w-full min-w-0 flex-row items-center justify-between gap-3 pr-2">
              <div className="flex flex-row items-center gap-2">
                <ZendeskIcon className="size-4 shrink-0" />
                <div className="truncate text-sm">{t("support.timeline.zendeskAudit.title")}</div>
                <span className="truncate text-sm">
                  {"[#"}
                  {zendeskIntegration.externalId}
                  {"]"}
                </span>
              </div>
              <div className="flex shrink-0 flex-row items-center gap-4">
                {data?.ticket.archived ? (
                  <Badge variant="warning" className="shrink-0">
                    {t("support.timeline.zendeskAudit.archived")}
                  </Badge>
                ) : (
                  data?.ticket.status && (
                    <Badge
                      variant="outline"
                      className="shrink-0 capitalize bg-support-primary/5 border-support-primary text-support-primary"
                    >
                      {data.ticket.status}
                    </Badge>
                  )
                )}
                <div className="flex flex-row items-center gap-2">
                  {isFetching && (
                    <RefreshCw className="size-3.5 animate-spin text-muted-foreground" />
                  )}
                  {fetchedAt && (
                    <span className="hidden text-xs text-muted-foreground sm:inline">
                      {t("support.timeline.zendeskAudit.fetchedAt", { time: fetchedAt })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="flex flex-col gap-4 pt-2">
            {isPending && <LoadingSlate />}
            {isError && <EmptySlate message={t("support.timeline.zendeskAudit.fetchError")} />}
            {data && (
              <div className="flex flex-col gap-4">
                <Tabs defaultValue="messages">
                  <TabsList>
                    <TabsTrigger value="messages">
                      <MessageSquareText className="size-4" />
                      <span>{t("support.timeline.zendeskAudit.messages")}</span>
                      <Badge variant="secondary">{messageEvents.length}</Badge>
                    </TabsTrigger>
                    <TabsTrigger value="changes">
                      <GitCompareArrows className="size-4" />
                      <span>{t("support.timeline.zendeskAudit.changes")}</span>
                      <Badge variant="secondary">{changeAudits.length}</Badge>
                    </TabsTrigger>
                  </TabsList>
                  <TabsContent value="messages" className="flex flex-col gap-2">
                    {messageEvents.length === 0 ? (
                      <EmptySlate message={t("support.timeline.zendeskAudit.emptyMessages")} />
                    ) : (
                      messageEvents.map((item, index) => (
                        <MessageRow
                          key={`${item.audit.id}-${item.event.type}-${index}`}
                          item={item}
                        />
                      ))
                    )}
                  </TabsContent>
                  <TabsContent value="changes" className="flex flex-col gap-2">
                    {changeAudits.length === 0 ? (
                      <EmptySlate message={t("support.timeline.zendeskAudit.emptyChanges")} />
                    ) : (
                      changeAudits.map((item) => <ChangeEventRow key={item.audit.id} item={item} />)
                    )}
                  </TabsContent>
                </Tabs>
                <Separator />
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-3">
                    <span className="shrink-0 pt-0.5 text-xs text-muted-foreground">
                      {t("support.timeline.zendeskAudit.currentTags")}
                      {":"}
                    </span>
                    <AuditValue value={data.ticket.tags} />
                  </div>
                </div>
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  )
}

function isChangeEvent(event: ZendeskAuditEvent) {
  if (!event.fieldName) return false
  if (event.type !== "Create" && event.type !== "Change") return false

  return AUDIT_CHANGE_FIELDS.has(event.fieldName)
}

function isMessageEvent(event: ZendeskAuditEvent) {
  return event.type === "Comment"
}

function getFieldLabel(fieldName: string | null, t: ReturnType<typeof useTranslation>["t"]) {
  if (!fieldName) return t("support.timeline.zendeskAudit.fields.unknown")

  return t(`support.timeline.zendeskAudit.fields.${fieldName}`, { defaultValue: fieldName })
}

function AuditValue({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "") {
    return <span className="text-xs text-muted-foreground">{"-"}</span>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-xs text-muted-foreground">{"-"}</span>
    }

    if (value.every((item) => typeof item !== "object")) {
      return (
        <span className="flex flex-row flex-wrap gap-1">
          {value.map((item, index) => (
            <Badge key={`${String(item)}-${index}`} variant="secondary">
              {String(item)}
            </Badge>
          ))}
        </span>
      )
    }
  }

  if (typeof value === "object") {
    return (
      <code className="text-xs bg-muted px-1.5 py-0.5 rounded-md break-all">
        {JSON.stringify(value)}
      </code>
    )
  }

  return <span className="text-xs">{String(value)}</span>
}

function EmptySlate({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-dashed px-3 py-4 text-sm text-muted-foreground">
      {message}
    </div>
  )
}

function LoadingSlate() {
  return (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-14 w-full" />
      <Skeleton className="h-14 w-full" />
    </div>
  )
}

function ChangeEventRow({ item }: { item: ZendeskChangeAudit }) {
  const { t } = useTranslation()
  const { audit, events } = item
  const title = events.some((event) => event.type === "Create")
    ? t("support.timeline.zendeskAudit.ticketCreated")
    : t("support.timeline.zendeskAudit.ticketUpdated")

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-row items-center gap-2">
          <GitCompareArrows className="size-3.5 text-muted-foreground" />
          <span className="text-sm font-medium">{title}</span>
        </div>
        <div className="flex flex-row flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{formatDate(parseISO(audit.createdAt), "Pp")}</span>
          {audit.via && <Badge variant="outline">{audit.via}</Badge>}
        </div>
      </div>
      <div className="flex flex-col divide-y">
        {events.map((event, index) => {
          const hasPreviousValue = event.previousValue !== null

          return (
            <div
              key={`${event.type}-${event.fieldName}-${index}`}
              className="grid gap-2 py-2 first:pt-0 last:pb-0 sm:grid-cols-[160px_1fr]"
            >
              <span className="text-xs font-medium text-muted-foreground">
                {getFieldLabel(event.fieldName, t)}
              </span>
              <div className="flex min-w-0 flex-row flex-wrap items-center gap-2">
                {hasPreviousValue && (
                  <>
                    <AuditValue value={event.previousValue} />
                    <span className="text-xs text-muted-foreground">{"->"}</span>
                  </>
                )}
                <AuditValue value={event.value} />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function MessageRow({ item }: { item: ZendeskMessageEvent }) {
  const { t } = useTranslation()
  const { audit, event } = item
  let messageKind = t("support.timeline.zendeskAudit.message")

  if (event.public === false) {
    messageKind = t("support.timeline.zendeskAudit.internalNote")
  }
  if (event.public === true) {
    messageKind = t("support.timeline.zendeskAudit.publicReply")
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-background p-3">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-row items-center gap-2">
          <span className="text-sm font-medium">{messageKind}</span>
        </div>
        <span className="text-xs text-muted-foreground">
          {formatDate(parseISO(audit.createdAt), "Pp")}
        </span>
      </div>
      <p className="whitespace-pre-wrap text-sm text-muted-foreground">
        {event.body || t("support.timeline.zendeskAudit.emptyMessage")}
      </p>
      {event.attachmentsCount > 0 && (
        <span className="text-xs text-muted-foreground">
          {t("support.timeline.zendeskAudit.attachments", {
            count: event.attachmentsCount,
          })}
        </span>
      )}
    </div>
  )
}
